<!--
  Sollae Cloud - Main Application Component (App.vue)
  솔래 클라우드 - 메인 애플리케이션 컴포넌트 (App.vue)

  English:
  - What: The main component of the Vue app.
  - Usage: It is the main container for all pages and sets the basic layout.
  - Feature Support: Handles the main layout (Header, Sidebar, Footer), global messages (Toasts, Modals), and Alarm sounds.
  - Why: It is used to manage global events and UI parts that stay visible on all pages.
  - Linked Files: PageLayout.vue, apiMixin.js, EventBus (utils.js), and Vue Router.
  - Editing Guide: 
    - To change global event listeners (like device updates), check the 'created' part.
    - To change the main layout or background, check the <template> and CSS.

  한국어:
  - 역할: Vue 애플리케이션의 루트 컴포넌트입니다.
  - 용도: 모든 페이지의 메인 래퍼 역할을 하며 전역 레이아웃 구조를 제공합니다.
  - 지원 기능: 핵심 레이아웃(헤더, 사이드바, 푸터), 전역 UI 피드백(토스트, 모달), 알람 사운드 등을 지원합니다.
  - 필요성: 모든 경로에서 유지되어야 하는 전역 상태 리스너 및 UI 요소를 중앙에서 관리하기 위해 필요합니다.
  - 연결된 파일: PageLayout.vue, apiMixin.js, EventBus (utils.js), 그리고 Vue Router를 사용합니다.
  - 수정 가이드:
    - 전역 이벤트 리스너(예: 디바이스 업데이트)를 수정하려면 'created' 훅을 확인하세요.
    - 메인 레이아웃이나 배경을 수정하려면 <template> 및 관련 CSS를 확인하세요.
-->
<template>
  <div>
    <!-- Preloader shown until user session is validated -->
    <div v-show="!userEmail" class="sollae-preloader"></div>
    
    <!-- Main layout component (Header, Navigation) -->
    <page-layout v-show="userEmail" />
    
    <!-- Dynamic Content Area -->
    <div
      v-show="userEmail"
      :class="[
        'children-content',
        {
          'non-fixed-position': isExpanding,
          'float-over': isStudio && !isExpanding,
        },
      ]"
    >
      <!-- Renders the component matching the current route -->
      <router-view @fetch-api="init" />
    </div>

    <!-- Global UI Feedback Components -->
    <common-modal />
    <common-toast />
    <common-footer />
    <progress-bar />
    
    <!-- Alarm sound for notifications (Desktop only) -->
    <alarm-sound v-if="!isMobile" />
    
    <!-- Slide-out menu for device settings -->
    <device-setting-menu v-if="showSettingMenu" />
  </div>
</template>

<script>
// Global Style Imports (전역 스타일시트 임포트)
// Defines global UI styles and common CSS classes.
import '@/assets/css/phpoc-common.css';

// Logic & Utilities (로직 및 유틸리티)
import apiMixin from '@/mixins/apiMixin'; // Shared API methods (데이터 통신을 위한 공용 메서드)
import { EventBus } from '@/js/utils'; // Global communication bus (전역 이벤트 통신 버스)
import { removeLoader } from '@/js/staticUtils'; // Utility to remove the initial preloader (초기 로딩 화면 제거 유틸리티)
import PageLayout from '@/components/layout/PageLayout'; // Header and Navigation layout (헤더 및 내비게이션 레이아웃)
import ErrorComponent from '@/components/common/AsyncError'; // Global error feedback component (전역 에러 표시 컴포넌트)

/**
 * Async Component: DeviceSettingMenu
 * 디바이스 설정 메뉴용 비동기 컴포넌트
 * Loaded only when needed to reduce initial bundle size. (초기 번들 크기를 줄이기 위해 필요할 때만 로드됨)
 */
const DeviceSettingMenu = () => ({
  component: import('@/components/device/DeviceSettingMenu'),
  error: ErrorComponent,
  timeout: 5000,
});

export default {
  name: 'UserPage',
  components: {
    PageLayout,
    DeviceSettingMenu,
  },
  mixins: [apiMixin],
  
  computed: {
    /**
     * Controls visibility of the device settings side-menu.
     */
    showSettingMenu() {
      return this.$store.getters['ports/settingView'];
    },
    
    /**
     * Checks if the current view should be displayed in full-screen mode.
     */
    isExpanding() {
      return this.$route.meta.fullScreen;
    },
    
    /**
     * Identifies if the user is currently in the "Studio" section.
     */
    isStudio() {
      return /studio/.test(this.$route.fullPath);
    },
    
    /**
     * Retrieves the current user's email from the Vuex store.
     * Used as a proxy to check if the user is logged in.
     */
    userEmail() {
      return this.$store.getters['user/userEmail'];
    },
  },

  methods: {
    /**
     * Initializes the user's device list. Called via @fetch-api event from router-view.
     */
    init(params) {
      this.initUserDeviceList(params);
    },
    
    /**
     * Updates the global wrapper size in the store, triggered on window resize.
     */
    updateSize() {
      this.$store.dispatch('local/setWrapperSize', new Date().getTime());
    },
  },

  /**
   * Lifecycle Hook: created
   * Sets up global event listeners for the application lifecycle.
   */
  created() {
    // Shared user preferences
    this.$store.dispatch('local/getSortby');
    this.$store.dispatch('local/getDeviceTab');

    // --- Global EventBus Listeners ---
    // These listeners allow components to trigger global actions or data fetches.
    EventBus.$on('fetch-ports', this.getPortsDataAPI);
    EventBus.$on('fetch-share-ports', this.getSharePortsDataAPI);
    EventBus.$on('fetch-device', this.getIncreasedDeviceList);
    EventBus.$on('remove-device', this.getDecreasedDeviceList);
    EventBus.$on('fetch-firmware', this.getPkgwareInfo);
    EventBus.$on('fetch-log-download', this.retrieveLogDownloadLink);
    EventBus.$on('fetch-daily-log-data', this.retrieveDailyLogData);
    EventBus.$on('fetch-hourly-log-data', this.retrieveHourlyLogData);
    EventBus.$on('set-userinfo', this.updateUserInfo);
    EventBus.$on('get-userinfo', this.retrieveUserInfo);
    EventBus.$on('set-notification-email', this.updateNotificationEmail);
    EventBus.$on('retrieve-devicelist', this.retrieveDeviceListAPI);

    // Device & Modbus Settings
    EventBus.$on('update-device-setting', this.updateDeviceSetting);
    EventBus.$on('update-modbus-setting', this.updateModbusSetting);
    EventBus.$on('add-modbus-slave', this.addModbusSlave);
    EventBus.$on('edit-modbus-slave', this.editModbusSlave);
    EventBus.$on('remove-modbus-slave', this.removeModbusSlave);
    
    // Account Management
    EventBus.$on('delete-account', this.deleteUserAccount);

    // Shared Device Access
    EventBus.$on('remove-share-user-device', this.removeShareUserDevice);
    EventBus.$on('add-share-user-device', this.addShareUserDevice);

    // Window Events
    window.onresize = this.updateSize;
    
    // Finalize loading
    removeLoader();
  },

  /**
   * Lifecycle Hook: beforeDestroy
   * Important: Clean up EventBus listeners to prevent memory leaks and duplicate events.
   */
  beforeDestroy() {
    EventBus.$off('fetch-ports');
    EventBus.$off('fetch-share-ports');
    EventBus.$off('fetch-device');
    EventBus.$off('remove-device');
    EventBus.$off('fetch-firmware');
    EventBus.$off('set-userinfo');
    EventBus.$off('get-userinfo');
    EventBus.$off('set-notification-email');
    EventBus.$off('retrieve-devicelist');
    EventBus.$off('fetch-log-download');
    EventBus.$off('fetch-daily-log-data');
    EventBus.$off('fetch-hourly-log-data');

    EventBus.$off('update-device-setting');
    EventBus.$off('update-modbus-setting');
    EventBus.$off('add-modbus-slave');
    EventBus.$off('edit-modbus-slave');
    EventBus.$off('remove-modbus-slave');
    EventBus.$off('delete-account');

    EventBus.$off('remove-share-user-device');
    EventBus.$off('add-share-user-device');

    window.onresize = null;
  },
};

</script>
